import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, gql } from '@apollo/client';
import {
    Box,
    Card,
    CardContent,
    Grid,
    Typography,
    TextField,
    InputAdornment,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Chip,
    Stack,
    Paper,
    LinearProgress,
    Alert,
    IconButton,
    Tooltip,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Snackbar,
} from '@mui/material';
import {
    Search as SearchIcon,
    Sort as SortIcon,
    FilterList as FilterIcon,
    Business as BusinessIcon,
    People as PeopleIcon,
    QrCodeScanner as QrCodeIcon,
    TrendingUp as TrendingUpIcon,
    TrendingDown as TrendingDownIcon,
    Add as AddIcon,
    Close as CloseIcon,
    Upload as UploadIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router';
import LeadGenCard from './LeadGenCard';
import MainCard from 'ui-component/cards/MainCard';
import { GET_ME } from 'graphql/queries';
import SendEmailToStand from '../../Components/SendEmailToStand';

const GET_STANDS_BY_EVENT_ID = gql`
    query GetStandsByEventId($eventId: String!) {
        standsByEventId(eventId: $eventId)
    }
`;

const CREATE_STAND = gql`
    mutation CreateStand($eventId: String!, $input: CreateStandInput!) {
        createStand(eventId: $eventId, input: $input)
    }
`;

const UPDATE_STAND = gql`
    mutation UpdateStand($eventId: String!, $input: UpdateStandInput!) {
        updateStand(eventId: $eventId, input: $input)
    }
`;

export default function LeadGen() {
    const { eventId } = useParams();
    const { loading, error, data, refetch } = useQuery(GET_STANDS_BY_EVENT_ID, {
        variables: { eventId },
    });
    const { data: userData } = useQuery(GET_ME);
    const navigate = useNavigate();

    const isSuperAdmin = userData?.me?.permissions?.includes('superadmin');

    const [searchTerm, setSearchTerm] = useState('');
    const [sortBy, setSortBy] = useState('company');
    const [sortOrder, setSortOrder] = useState('asc');
    const [filterBy, setFilterBy] = useState('all');
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingStand, setEditingStand] = useState(null);
    const [formData, setFormData] = useState({
        company: '',
        email: '',
        password: '',
        allowed_no_of_staff: '',
        stallNo: '',
    });
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [sendEmailDialogOpen, setSendEmailDialogOpen] = useState(false);
    const [selectedStandForEmail, setSelectedStandForEmail] = useState(null);

    const stands = (data && data?.standsByEventId && JSON.parse(data?.standsByEventId)) || [];

    // Process and filter stands
    const processedStands = useMemo(() => {
        let processed = stands.map(stand => {
            // Use totalScans from backend if available, otherwise calculate
            const totalScans =
                stand.totalScans !== undefined
                    ? stand.totalScans
                    : stand.staff?.reduce((total, staff) => {
                          return total + (staff.scansCount || 0);
                      }, 0) || 0;

            return {
                ...stand,
                totalScans,
                staffCount: stand.staff?.length || 0,
            };
        });

        // Apply search filter
        if (searchTerm) {
            processed = processed.filter(
                stand =>
                    stand.company?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    stand.staff?.some(staff => staff.name?.toLowerCase().includes(searchTerm.toLowerCase()))
            );
        }

        // Apply filter
        if (filterBy === 'withStaff') {
            processed = processed.filter(stand => stand.staffCount > 0);
        } else if (filterBy === 'withScans') {
            processed = processed.filter(stand => stand.totalScans > 0);
        } else if (filterBy === 'noStaff') {
            processed = processed.filter(stand => stand.staffCount === 0);
        } else if (filterBy === 'noScans') {
            processed = processed.filter(stand => stand.totalScans === 0);
        }

        // Apply sorting
        processed.sort((a, b) => {
            let aValue, bValue;

            switch (sortBy) {
                case 'company':
                    aValue = a.company || '';
                    bValue = b.company || '';
                    break;
                case 'staffCount':
                    aValue = a.staffCount || 0;
                    bValue = b.staffCount || 0;
                    break;
                case 'totalScans':
                    aValue = a.totalScans || 0;
                    bValue = b.totalScans || 0;
                    break;
                default:
                    aValue = a.company || '';
                    bValue = b.company || '';
            }

            if (typeof aValue === 'string') {
                return sortOrder === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
            } else {
                return sortOrder === 'asc' ? aValue - bValue : bValue - aValue;
            }
        });

        return processed;
    }, [stands, searchTerm, sortBy, sortOrder, filterBy]);

    const [createStand] = useMutation(CREATE_STAND, {
        onCompleted: () => {
            setSnackbar({ open: true, message: 'Leadgen account created successfully', severity: 'success' });
            setDialogOpen(false);
            resetForm();
            refetch();
        },
        onError: error => {
            setSnackbar({ open: true, message: error.message || 'Failed to create account', severity: 'error' });
        },
    });

    const [updateStand] = useMutation(UPDATE_STAND, {
        onCompleted: () => {
            setSnackbar({ open: true, message: 'Leadgen account updated successfully', severity: 'success' });
            setDialogOpen(false);
            resetForm();
            refetch();
        },
        onError: error => {
            setSnackbar({ open: true, message: error.message || 'Failed to update account', severity: 'error' });
        },
    });

    const resetForm = () => {
        setFormData({
            company: '',
            email: '',
            password: '',
            allowed_no_of_staff: '',
            stallNo: '',
        });
        setEditingStand(null);
    };

    const handleOpenDialog = (stand = null) => {
        if (stand) {
            setEditingStand(stand);
            setFormData({
                company: stand.company || '',
                email: stand.email || '',
                password: '', // Don't pre-fill password
                allowed_no_of_staff: stand.allowed_no_of_staff || '',
                stallNo: stand.stallNo || '',
            });
        } else {
            resetForm();
        }
        setDialogOpen(true);
    };

    const handleCloseDialog = () => {
        setDialogOpen(false);
        resetForm();
    };

    const handleSubmit = () => {
        if (!formData.company || !formData.email || !formData.allowed_no_of_staff) {
            setSnackbar({
                open: true,
                message: 'Please fill in all required fields (Company Name, Email, and Allowed No of Staff)',
                severity: 'error',
            });
            return;
        }

        if (editingStand) {
            // Update existing stand
            updateStand({
                variables: {
                    eventId,
                    input: {
                        id: editingStand.id, // ID is required for update, but not shown in form
                        company: formData.company,
                        email: formData.email,
                        password: formData.password || undefined, // Only update if provided
                        allowed_no_of_staff: formData.allowed_no_of_staff || '0',
                        stallNo: formData.stallNo || undefined,
                    },
                },
            });
        } else {
            // Create new stand
            if (!formData.password) {
                setSnackbar({ open: true, message: 'Password is required for new accounts', severity: 'error' });
                return;
            }
            // ID will be auto-generated by backend
            createStand({
                variables: {
                    eventId,
                    input: {
                        // id and parentId will be auto-generated by backend
                        company: formData.company,
                        email: formData.email,
                        password: formData.password,
                        name: formData.company, // Use company name as name
                        allowed_no_of_staff: formData.allowed_no_of_staff || '0',
                        stallNo: formData.stallNo || undefined,
                    },
                },
            });
        }
    };

    if (loading) {
        return (
            <MainCard title='Lead Generation Data'>
                <LinearProgress />
            </MainCard>
        );
    }

    if (error) {
        return (
            <MainCard title='Lead Generation Data'>
                <Alert severity='error'>{error.message}</Alert>
            </MainCard>
        );
    }

    return (
        <MainCard title='Lead Generation Data'>
            <Box sx={{ mb: 4 }}>
                <Stack direction='row' justifyContent='space-between' alignItems='center' sx={{ mb: 3 }}>
                    <Typography variant='body2' color='text.secondary'>
                        Manage and view lead generation data for all exhibitors. Search, filter, and sort to find
                        specific stands.
                    </Typography>
                    <Stack direction='row' spacing={2}>
                        {(isSuperAdmin || userData?.me?.permissions?.includes('admin')) && (
                            <Button
                                variant='outlined'
                                startIcon={<UploadIcon />}
                                onClick={() => navigate('import')}
                                sx={{ minWidth: 180 }}
                            >
                                Import Stands
                            </Button>
                        )}
                        {isSuperAdmin && (
                            <Button
                                variant='contained'
                                startIcon={<AddIcon />}
                                onClick={() => handleOpenDialog()}
                                sx={{ minWidth: 200 }}
                            >
                                Add New Leadgen Account
                            </Button>
                        )}
                    </Stack>
                </Stack>

                {/* Search and Filter Section */}
                <Paper
                    elevation={2}
                    sx={{
                        p: 3,
                        mb: 3,
                        borderRadius: 2,
                        background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                        border: '1px solid',
                        borderColor: 'primary.light',
                    }}
                >
                    <Grid container spacing={3} alignItems='center'>
                        {/* Search Field */}
                        <Grid item xs={12} md={4}>
                            <TextField
                                fullWidth
                                size='small'
                                placeholder='🔍 Search by company or staff name...'
                                value={searchTerm}
                                onChange={e => setSearchTerm(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position='start'>
                                            <SearchIcon />
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        bgcolor: 'white',
                                        '& fieldset': {
                                            borderColor: 'primary.main',
                                        },
                                        '&:hover fieldset': {
                                            borderColor: 'primary.dark',
                                        },
                                    },
                                }}
                            />
                        </Grid>

                        {/* Sort By */}
                        <Grid item xs={12} md={3}>
                            <FormControl fullWidth size='small'>
                                <InputLabel>Sort By</InputLabel>
                                <Select
                                    value={sortBy}
                                    onChange={e => setSortBy(e.target.value)}
                                    label='Sort By'
                                    startAdornment={<SortIcon sx={{ mr: 1, color: 'text.secondary' }} />}
                                    sx={{ bgcolor: 'white' }}
                                >
                                    <MenuItem value='company'>
                                        <Stack direction='row' spacing={1} alignItems='center'>
                                            <BusinessIcon fontSize='small' />
                                            <span>Company Name</span>
                                        </Stack>
                                    </MenuItem>
                                    <MenuItem value='staffCount'>
                                        <Stack direction='row' spacing={1} alignItems='center'>
                                            <PeopleIcon fontSize='small' />
                                            <span>Number of Staff</span>
                                        </Stack>
                                    </MenuItem>
                                    <MenuItem value='totalScans'>
                                        <Stack direction='row' spacing={1} alignItems='center'>
                                            <QrCodeIcon fontSize='small' />
                                            <span>Total Scans</span>
                                        </Stack>
                                    </MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>

                        {/* Sort Order */}
                        <Grid item xs={12} md={2}>
                            <FormControl fullWidth size='small'>
                                <InputLabel>Order</InputLabel>
                                <Select
                                    value={sortOrder}
                                    onChange={e => setSortOrder(e.target.value)}
                                    label='Order'
                                    sx={{ bgcolor: 'white' }}
                                >
                                    <MenuItem value='asc'>
                                        <Stack direction='row' spacing={1} alignItems='center'>
                                            <TrendingUpIcon fontSize='small' />
                                            <span>Ascending</span>
                                        </Stack>
                                    </MenuItem>
                                    <MenuItem value='desc'>
                                        <Stack direction='row' spacing={1} alignItems='center'>
                                            <TrendingDownIcon fontSize='small' />
                                            <span>Descending</span>
                                        </Stack>
                                    </MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>

                        {/* Filter */}
                        <Grid item xs={12} md={3}>
                            <FormControl fullWidth size='small'>
                                <InputLabel>Filter</InputLabel>
                                <Select
                                    value={filterBy}
                                    onChange={e => setFilterBy(e.target.value)}
                                    label='Filter'
                                    startAdornment={<FilterIcon sx={{ mr: 1, color: 'text.secondary' }} />}
                                    sx={{ bgcolor: 'white' }}
                                >
                                    <MenuItem value='all'>All Stands</MenuItem>
                                    <MenuItem value='withStaff'>With Staff</MenuItem>
                                    <MenuItem value='withScans'>With Scans</MenuItem>
                                    <MenuItem value='noStaff'>No Staff</MenuItem>
                                    <MenuItem value='noScans'>No Scans</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>
                </Paper>

                {/* Results Summary */}
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mb: 3,
                        px: 2,
                        py: 2,
                        bgcolor: 'rgba(102, 126, 234, 0.05)',
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'primary.light',
                    }}
                >
                    <Chip
                        label={`📊 Total Stands: ${processedStands.length}`}
                        size='small'
                        sx={{
                            bgcolor: 'primary.main',
                            color: 'white',
                            fontWeight: 600,
                            fontSize: '0.875rem',
                        }}
                    />
                    {searchTerm && (
                        <Chip
                            label={`🔍 Search: "${searchTerm}"`}
                            size='small'
                            onDelete={() => setSearchTerm('')}
                            sx={{
                                borderColor: 'primary.main',
                                color: 'primary.main',
                            }}
                            variant='outlined'
                        />
                    )}
                    {filterBy !== 'all' && (
                        <Chip
                            label={`🔽 Filter: ${filterBy}`}
                            size='small'
                            onDelete={() => setFilterBy('all')}
                            sx={{
                                borderColor: 'primary.main',
                                color: 'primary.main',
                            }}
                            variant='outlined'
                        />
                    )}
                </Box>

                {/* Stands Grid */}
                {processedStands.length > 0 ? (
                    <Grid container spacing={3}>
                        {processedStands.map((stand, index) => (
                            <Grid item xs={12} sm={6} md={4} lg={3} xl={2} key={stand.id || index}>
                                <LeadGenCard
                                    stand={stand}
                                    type='admin'
                                    totalScans={stand.totalScans}
                                    onEdit={isSuperAdmin ? () => handleOpenDialog(stand) : null}
                                    onSendEmail={() => {
                                        setSelectedStandForEmail(stand);
                                        setSendEmailDialogOpen(true);
                                    }}
                                />
                            </Grid>
                        ))}
                    </Grid>
                ) : (
                    <Alert severity='info' sx={{ mt: 3 }}>
                        {searchTerm || filterBy !== 'all'
                            ? 'No stands match your search or filter criteria.'
                            : 'No stands available.'}
                    </Alert>
                )}

                {/* Create/Edit Dialog */}
                <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth='md' fullWidth>
                    <DialogTitle>
                        <Stack direction='row' justifyContent='space-between' alignItems='center'>
                            <Typography variant='h6'>
                                {editingStand ? 'Edit Leadgen Account' : 'Add New Leadgen Account'}
                            </Typography>
                            <IconButton onClick={handleCloseDialog}>
                                <CloseIcon />
                            </IconButton>
                        </Stack>
                    </DialogTitle>
                    <DialogContent>
                        <Stack spacing={3} sx={{ mt: 2 }}>
                            {editingStand && (
                                <TextField
                                    label='ID'
                                    value={formData.id}
                                    disabled
                                    helperText='ID is auto-generated and cannot be changed'
                                    fullWidth
                                />
                            )}
                            <TextField
                                label='Company Name'
                                value={formData.company}
                                onChange={e => setFormData({ ...formData, company: e.target.value })}
                                required
                                fullWidth
                            />
                            <TextField
                                label='Email'
                                type='email'
                                value={formData.email}
                                onChange={e => setFormData({ ...formData, email: e.target.value })}
                                required
                                fullWidth
                            />
                            <TextField
                                label={editingStand ? 'New Password (leave blank to keep current)' : 'Password'}
                                type='password'
                                value={formData.password}
                                onChange={e => setFormData({ ...formData, password: e.target.value })}
                                required={!editingStand}
                                fullWidth
                            />
                            <TextField
                                label='Allowed Number of Staff'
                                type='number'
                                value={formData.allowed_no_of_staff}
                                onChange={e => setFormData({ ...formData, allowed_no_of_staff: e.target.value })}
                                required
                                fullWidth
                            />
                            <TextField
                                label='Stall Number (Optional)'
                                value={formData.stallNo}
                                onChange={e => setFormData({ ...formData, stallNo: e.target.value })}
                                fullWidth
                            />
                        </Stack>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCloseDialog}>Cancel</Button>
                        <Button variant='contained' onClick={handleSubmit}>
                            {editingStand ? 'Update' : 'Create'}
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Snackbar */}
                <Snackbar
                    open={snackbar.open}
                    autoHideDuration={6000}
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                >
                    <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity}>
                        {snackbar.message}
                    </Alert>
                </Snackbar>

                {/* Send Email Dialog */}
                {selectedStandForEmail && (
                    <SendEmailToStand
                        open={sendEmailDialogOpen}
                        onClose={() => {
                            setSendEmailDialogOpen(false);
                            setSelectedStandForEmail(null);
                        }}
                        stand={selectedStandForEmail}
                    />
                )}
            </Box>
        </MainCard>
    );
}
